import React from "react"
import {
    Tag,
    TagPicker as TagPickerComponent,
    TagPickerSuggestion,
    TagResult,
} from "@tenforce/product-fields"
import {
    createTag,
    deleteTag,
    getAllTags,
    getSelectedTags,
    getTagByExactName,
    sendTagPickerDeltaValue,
    updateTag,
} from "../../../../apis/tagPicker"
import { Fields } from "../.."

declare let window: OldUIContext.TenForceWindow

interface Props extends Fields.TagPickerField {
    itemId: number
}

export default function TagPicker(props: Props): JSX.Element {
    const [selectedTags, setSelectedTags] = React.useState<Tag[]>([])

    const {
        itemId,
        fieldId,
        value: selectedTagsFromProps,
        title,
        description,
        isReadOnly,
        isRequired,
    } = props

    React.useEffect(() => {
        setSelectedTags(selectedTagsFromProps)
    }, [selectedTagsFromProps])

    const getTagByName = React.useCallback(
        async (tagName: string) => {
            return await getTagByExactName(itemId, fieldId, tagName)
        },
        [itemId, fieldId],
    )

    const getSuggestions = React.useCallback(
        async (query: string) => {
            return (await getAllTags(itemId, fieldId, query, 0, 20))
                .tags as TagPickerSuggestion[]
        },
        [itemId, fieldId],
    )

    const fetchTagResult = React.useCallback(
        async (filter: string): Promise<TagResult> => {
            return await getAllTags(itemId, fieldId, filter, 0, 20)
        },
        [],
    )

    const loadMoreTagResult = React.useCallback(
        async (filter: string, offset: number) => {
            return (await getAllTags(itemId, fieldId, filter, offset, 20)).tags
        },
        [],
    )

    const onSave = React.useCallback(
        async (tags: Tag[] | null) => {
            if (!tags) {
                setSelectedTags(await getSelectedTags(itemId, fieldId))
                return
            }

            const localSelectedTagIds = selectedTags.map(
                (tag) => tag.id as number,
            )
            const newSelectedTagIds = tags
                .filter((tag) => !tag.fresh)
                .map((tag) => Number(tag.id))

            const newlyCreatedTags = tags.filter((tag) => tag.fresh)
            const newSelectedSet = new Set(newSelectedTagIds)
            const localSelectedSet = new Set(localSelectedTagIds)

            const toDeselect = localSelectedTagIds.filter(
                (id) => !newSelectedSet.has(id),
            )

            const toSelect = newSelectedTagIds.filter(
                (id) => !localSelectedSet.has(id),
            )

            toSelect.push(
                ...(await Promise.all(
                    newlyCreatedTags.map((tag) =>
                        createTag(tag).then((tag) => tag.id as number),
                    ),
                )),
            )

            if (!toDeselect.length && !toSelect.length) {
                return
            }

            await sendTagPickerDeltaValue(toSelect, toDeselect, itemId, fieldId)
            const result = await getSelectedTags(itemId, fieldId)
            setSelectedTags(result)
            return
        },
        [itemId, fieldId, selectedTags],
    )

    return (
        <TagPickerComponent
            fieldOptions={{
                fieldId: fieldId,
                title: title,
                description: description,
                isReadOnly: isReadOnly,
                isRequired: isRequired,
            }}
            selectedTags={selectedTags}
            totalTagsCount={10}
            updateTagData={updateTag}
            fetchTagByName={getTagByName}
            fetchSuggestions={getSuggestions}
            fetchTagResult={fetchTagResult}
            loadMoreTagResult={loadMoreTagResult}
            deleteTag={deleteTag}
            onSave={onSave}
            searchForItems={(tag): void => {
                window.env.searchTag(`${tag.id}`)
            }}
        />
    )
}
